import { useState } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { EndpointHint, HelpIcon, LoadFailure, Skeleton } from '../../core/index.js'
import type { CensusView } from '../types.js'
import { censusTrend, monthBounds, trendScale, type TrendDay, type TrendSeries } from './monthTrendsData.js'

/** "Tue, Dec 8". */
const dayLabel = (date: string): string => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
const weekdayName = (date: string): string => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })

function basisText(day: TrendDay, forecastWeeks: number): string {
  if (day.census === null) {
    return 'No count, and nothing to forecast from'
  }
  if (day.basis === 'recorded') {
    return 'Recorded by staff'
  }
  return day.basis === 'weekday_average' ? `Forecast: the ${weekdayName(day.date)} average of the last ${forecastWeeks} weeks` : 'Forecast: the planned census, with no recent counts'
}

function againstPlanned(census: number | null, planned: number | null): string {
  if (census === null || planned === null) {
    return '—'
  }
  return census === planned ? 'At the planned census' : census < planned ? `${planned - census} under planned` : `${census - planned} over planned`
}

/** Stroke paths between consecutive days with a count: solid where both were recorded, dashed where either is forecast. */
function segmentPaths(days: readonly TrendDay[], x: (index: number) => number, y: (census: number) => number): { recorded: string; forecast: string } {
  const recorded: string[] = []
  const forecast: string[] = []
  days.forEach((day, index) => {
    const next = days[index + 1]
    if (day.census === null || !next || next.census === null) {
      return
    }
    const piece = `M${x(index).toFixed(1)} ${y(day.census).toFixed(2)} L${x(index + 1).toFixed(1)} ${y(next.census).toFixed(2)}`
    ;(day.kind === 'recorded' && next.kind === 'recorded' ? recorded : forecast).push(piece)
  })
  return { recorded: recorded.join(' '), forecast: forecast.join(' ') }
}

interface MonthTrendsProps {
  query: UseQueryResult<CensusView>
  period: string
  /** Today in the facility's zone, `YYYY-MM-DD`: where recorded meets forecast. */
  today: string
}

/**
 * The Month tab's Trends panel (26 October 2026): the month's census by day as a line - the facility or one unit - solid
 * where staff recorded the count, dashed where it is the forecast, against the planned census the month's shifts were
 * published for. Hover or tab to a day for its count; Show as a table lists every day.
 */
export function MonthTrends({ query, period, today }: MonthTrendsProps) {
  const [series, setSeries] = useState<TrendSeries>('facility')
  const [hovered, setHovered] = useState<number | null>(null)
  const [table, setTable] = useState(false)
  const heading = (
    <div className="flex flex-wrap items-baseline gap-2">
      <h2 className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-ink-mid">
        Trends
        <HelpIcon topic="schedule.month-trends" />
      </h2>
      <EndpointHint>GET /facilities/{'{facilityId}'}/census?from&amp;to</EndpointHint>
    </div>
  )
  if (query.isError) {
    return (
      <section className="flex flex-col gap-2 rounded border border-marine/15 bg-white px-4 py-3.5">
        {heading}
        <LoadFailure size="xs" message="The census could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      </section>
    )
  }
  if (!query.data) {
    return (
      <section className="flex flex-col gap-2 rounded border border-marine/15 bg-white px-4 py-3.5">
        {heading}
        <Skeleton lines={5} label="Loading the census" />
      </section>
    )
  }
  const view = query.data
  if (view.units.length === 0) {
    return (
      <section className="flex flex-col gap-2 rounded border border-marine/15 bg-white px-4 py-3.5">
        {heading}
        <p className="text-xs text-ink-mid">The facility lists no units, so there is no census to chart. Units are set on Configuration › Details.</p>
      </section>
    )
  }
  const { from, to } = monthBounds(period)
  const chosen = series === 'facility' || view.units.some((unit) => unit.id === series) ? series : 'facility'
  const trend = censusTrend(view, chosen, from, to)
  const { days, planned, stats } = trend
  const counts = days.flatMap((day) => (day.census === null ? [] : [day.census]))
  const scale = trendScale(planned === null ? counts : [...counts, planned])
  const x = (index: number): number => (days.length <= 1 ? 0 : (index / (days.length - 1)) * 1000)
  const y = (census: number): number => (1 - (census - scale.min) / (scale.max - scale.min)) * 100
  const paths = segmentPaths(days, x, y)
  const left = (index: number): number => x(index) / 10
  const todayIndex = days.findIndex((day) => day.date === today)
  const recordedAny = days.some((day) => day.kind === 'recorded' && day.census !== null)
  const name = chosen === 'facility' ? 'The facility' : (view.units.find((unit) => unit.id === chosen)?.name ?? chosen)
  const monthName = new Date(`${period}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })
  const basisNote = today < from ? 'the month has not begun, so every day is forecast' : today > to ? (recordedAny ? 'as recorded' : 'no counts were recorded') : 'recorded through today, forecast after'
  const hover = hovered !== null ? days[hovered] : undefined
  const choices: { id: TrendSeries; label: string }[] = [{ id: 'facility', label: 'Facility' }, ...view.units.map((unit) => ({ id: unit.id, label: unit.name }))]
  const stat = (label: string, value: string, note: string) => (
    <div className="min-w-[8rem] flex-1 border-r border-marine/10 px-3 py-2 last:border-r-0">
      <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
      <div className="text-lg font-black leading-tight text-ink">{value}</div>
      <div className="text-[10px] text-ink-mid">{note}</div>
    </div>
  )

  return (
    <section className="flex min-w-0 flex-col gap-3 rounded border border-marine/15 bg-white px-4 py-3.5" aria-label="Trends">
      <div className="flex flex-wrap items-center gap-2">
        {heading}
        <span className="text-xs text-ink-mid">
          residents in beds each day of {monthName} · {basisNote}
        </span>
        <div className="flex-1" />
        <div role="group" aria-label="Census for" className="inline-flex overflow-hidden rounded border border-marine/30">
          {choices.map((choice) => (
            <button key={choice.id} type="button" aria-pressed={choice.id === chosen} onClick={() => { setSeries(choice.id); setHovered(null) }} className={`min-h-8 px-3 text-xs font-bold ${choice.id === chosen ? 'bg-marine text-white' : 'bg-white text-marine hover:bg-marine-lt'}`}>
              {choice.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap rounded border border-marine/10">
        {stat('Average', stats.average === null ? '—' : String(stats.average), 'residents a day')}
        {stat('Low', stats.low?.census != null ? String(stats.low.census) : '—', stats.low ? dayLabel(stats.low.date) : 'no counts')}
        {stat('High', stats.high?.census != null ? String(stats.high.census) : '—', stats.high ? dayLabel(stats.high.date) : 'no counts')}
        {stat('Planned census', planned === null ? '—' : String(planned), planned === null ? 'a unit has none set' : 'what the shifts were published for')}
        {stat('Days under planned', stats.underPlanned === null ? '—' : String(stats.underPlanned), `of ${stats.daysCounted} · patient days ${stats.patientDays.toLocaleString('en-US')}`)}
      </div>

      {counts.length === 0 ? (
        <p className="text-xs text-ink-mid">No counts and nothing to forecast from this month. Record the census, or set each unit's planned census on Configuration › Details.</p>
      ) : (
        <div className="grid grid-cols-[2.25rem_minmax(0,1fr)] gap-x-1.5">
          <div className="relative h-60" aria-hidden="true">
            {scale.ticks.map((tick) => (
              <span key={tick} className="absolute right-0 text-[10px] tabular-nums text-ink-mid" style={{ top: `calc(${y(tick)}% - 7px)` }}>
                {tick}
              </span>
            ))}
          </div>
          <div className="relative h-60" onMouseLeave={() => setHovered(null)}>
            {scale.ticks.map((tick) => (
              <div key={tick} aria-hidden="true" className={`absolute inset-x-0 border-t ${tick === scale.min ? 'border-marine/35' : 'border-marine/10'}`} style={{ top: `${y(tick)}%` }} />
            ))}
            <svg viewBox="0 0 1000 100" preserveAspectRatio="none" role="img" aria-label={`${name}'s census by day, ${monthName}: from ${stats.low?.census ?? '—'} to ${stats.high?.census ?? '—'} residents${planned !== null ? `, planned ${planned}` : ''}.`} className="absolute inset-0 h-full w-full overflow-visible">
              {planned !== null && <path d={`M0 ${y(planned).toFixed(2)} L1000 ${y(planned).toFixed(2)}`} fill="none" stroke="#7A94A0" strokeWidth={1.5} strokeDasharray="6 5" vectorEffect="non-scaling-stroke" />}
              <path d={paths.forecast} fill="none" stroke="#0C5E74" strokeOpacity={0.75} strokeWidth={2} strokeDasharray="5 4" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              <path d={paths.recorded} fill="none" stroke="#0C5E74" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            </svg>
            {planned !== null && (
              <span aria-hidden="true" className="pointer-events-none absolute right-1 bg-white/85 px-1 text-[10px] font-bold text-ink-mid" style={{ top: `calc(${y(planned)}% - 16px)` }}>
                Planned {planned}
              </span>
            )}
            {todayIndex >= 0 && (
              <>
                <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 border-l border-dashed border-coral/60" style={{ left: `${left(todayIndex)}%` }} />
                <span aria-hidden="true" className="pointer-events-none absolute -top-1 text-[10px] font-bold text-coral" style={{ left: `calc(${left(todayIndex)}% + 4px)` }}>
                  Today
                </span>
              </>
            )}
            {days.map((day, index) => (
              <div
                key={day.date}
                tabIndex={0}
                role="img"
                aria-label={`${dayLabel(day.date)}: ${day.census === null ? 'no count' : `${day.census} residents, ${day.kind}`}, ${againstPlanned(day.census, planned).toLowerCase()}`}
                onMouseEnter={() => setHovered(index)}
                onFocus={() => setHovered(index)}
                onBlur={() => setHovered(null)}
                className="absolute inset-y-0 cursor-crosshair outline-offset-[-2px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-marine"
                style={{ left: `calc(${left(index)}% - ${50 / days.length}%)`, width: `${100 / days.length}%` }}
              />
            ))}
            {hover && hovered !== null && (
              <>
                <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 border-l border-marine/35" style={{ left: `${left(hovered)}%` }} />
                {hover.census !== null && <div aria-hidden="true" className="pointer-events-none absolute h-3 w-3 rounded-full bg-marine ring-2 ring-white" style={{ left: `calc(${left(hovered)}% - 6px)`, top: `calc(${y(hover.census)}% - 6px)` }} />}
                <div aria-hidden="true" className="pointer-events-none absolute top-2 z-10 flex w-56 flex-col gap-0.5 rounded border border-marine/20 bg-white px-2.5 py-2 shadow-lg" style={left(hovered) > 70 ? { right: `calc(${100 - left(hovered)}% + 12px)` } : { left: `calc(${left(hovered)}% + 12px)` }}>
                  <span className="text-xs font-black text-ink">{dayLabel(hover.date)}</span>
                  <span className="text-lg font-black leading-tight text-ink">
                    {hover.census ?? '—'} <span className="text-[11px] font-bold text-ink-mid">residents</span>
                  </span>
                  <span className="text-[11px] leading-snug text-ink-mid">{basisText(hover, view.forecastWeeks)}</span>
                  <span className="text-[11px] leading-snug text-ink-mid">
                    {againstPlanned(hover.census, planned)}
                    {planned !== null ? ` (${planned})` : ''}
                  </span>
                </div>
              </>
            )}
          </div>
          <div />
          <div className="relative h-4" aria-hidden="true">
            {[1, 8, 15, 22, 29].filter((day) => day <= days.length).map((day) => (
              <span key={day} className="absolute top-0.5 -translate-x-1/2 whitespace-nowrap text-[10px] text-ink-mid" style={{ left: `${left(day - 1)}%` }}>
                {new Date(`${days[day - 1]?.date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-ink-mid">
        <span className="inline-flex items-center gap-1.5">
          <svg width="26" height="8" aria-hidden="true"><path d="M1 4h24" stroke="#0C5E74" strokeWidth="2" strokeLinecap="round" /></svg>
          Recorded
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="26" height="8" aria-hidden="true"><path d="M1 4h24" stroke="#0C5E74" strokeOpacity="0.75" strokeWidth="2" strokeDasharray="5 4" /></svg>
          Forecast: the same weekday's average of the last {view.forecastWeeks} weeks, else the planned census
        </span>
        {planned !== null && (
          <span className="inline-flex items-center gap-1.5">
            <svg width="26" height="8" aria-hidden="true"><path d="M1 4h24" stroke="#7A94A0" strokeWidth="1.5" strokeDasharray="6 5" /></svg>
            Planned census
          </span>
        )}
        <div className="flex-1" />
        <button type="button" aria-expanded={table} onClick={() => setTable(!table)} className="text-xs font-bold text-marine hover:text-marine-dk">
          {table ? 'Hide the table' : 'Show as a table'}
        </button>
      </div>

      {table && (
        <div className="max-h-64 overflow-auto rounded border border-marine/10">
          <table className="w-full text-xs">
            <thead>
              <tr className="sticky top-0 bg-marine-dk text-left text-[10px] font-bold uppercase tracking-wide text-white/75">
                <th className="px-2 py-1.5">Day</th>
                <th className="px-2 py-1.5">Residents</th>
                <th className="px-2 py-1.5">Basis</th>
                <th className="px-2 py-1.5">Against planned</th>
              </tr>
            </thead>
            <tbody>
              {days.map((day) => (
                <tr key={day.date} className="border-b border-marine/10">
                  <td className="px-2 py-1">{dayLabel(day.date)}</td>
                  <td className="px-2 py-1 font-bold text-ink">{day.census ?? '—'}</td>
                  <td className="px-2 py-1 text-ink-mid">{basisText(day, view.forecastWeeks)}</td>
                  <td className="px-2 py-1 text-ink-mid">{againstPlanned(day.census, planned)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
